<div class="payment-method-selection">
  <h2 class="mb-4">{{ 'payment_method_title' | translate }}</h2>

  <!-- Mini Order Summary -->
  <c-card class="order-summary-mini mb-4 shadow-sm">
    <c-card-body>
      @if (paymentCosts() <= 0) {
        <p class="mb-0 d-flex justify-content-between">
          <span>{{ 'payment_total_amount' | translate }}</span>
          <strong class="text-primary fs-4">
            {{ totalPrice | currencyAmount: currency() }}
          </strong>
        </p>
      } @else {
        <div>
          <p class="mb-1 d-flex justify-content-between">
            <span>{{ 'payment_subtotal' | translate }}</span>
            <span>{{ totalPrice | currencyAmount: currency() }}</span>
          </p>
          <p class="mb-1 text-body-secondary d-flex justify-content-between">
            <span>{{ 'payment_processing_fee' | translate }}</span>
            <span>{{ paymentCosts() | currencyAmount: currency() }}</span>
          </p>
          <hr class="my-2" />
          <p class="mb-0 d-flex justify-content-between">
            <strong>{{ 'payment_total_amount' | translate }}</strong>
            <strong class="text-primary fs-4">
              {{ totalWithCosts() | currencyAmount: currency() }}
            </strong>
          </p>
        </div>
      }
    </c-card-body>
  </c-card>

  <!-- Error -->
  @if (error()) {
    <div class="alert alert-danger mb-4">
      {{ error() }}
    </div>
  }

  <!-- Loading -->
  @if (loading()) {
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'product_loading' | translate }}</span>
      </div>
    </div>
  }

  <!-- License Confirmation (shown when cart contains LIC_ products) -->
  @if (!loading() && hasLicenseProducts()) {
    <div class="license-confirmation mb-4 p-3 border border-warning rounded">
      <div class="form-check">
        <input
          class="form-check-input"
          type="checkbox"
          id="licenseConfirmationCheck"
          [checked]="licenseConfirmed()"
          (change)="licenseConfirmed.set($any($event.target).checked)"
        />
        <label class="form-check-label small" for="licenseConfirmationCheck">
          {{ 'license_confirmation_text' | translate }}
        </label>
      </div>
    </div>
  }

  <!-- Payment Options -->
  @if (!loading()) {
    <div class="payment-methods">
      <!-- eWallet -->
      @if (isMethodEnabled('ewallet')) {
        <c-card
          class="payment-option mb-3 shadow-sm"
          [class.selected]="selectedMethod() === 'ewallet'"
          [class.disabled]="!ewalletAvailable()"
          (click)="selectMethod('ewallet')"
        >
          <c-card-body class="d-flex align-items-center">
            <div class="option-icon me-3">
              <svg cIcon name="cilWallet"></svg>
            </div>
            <div class="option-content flex-grow-1">
              <h3 class="mb-1">{{ 'payment_ewallet' | translate }}</h3>
              <p class="balance mb-0">
                {{ 'payment_ewallet_balance' | translate }}
                {{ userBalance() | currencyAmount: currency() }}
              </p>
              @if (!ewalletAvailable()) {
                <p class="insufficient text-danger mb-0 mt-1 d-flex align-items-center">
                  <svg cIcon name="cilExclamationCircle" class="me-1"></svg>
                  {{ 'payment_ewallet_insufficient' | translate }}
                </p>
              }
            </div>
            <!-- @if (selectedMethod() === 'ewallet') {
              <div class="option-check">
                <svg cIcon name="cilCheckCircle" color="success"></svg>
              </div>
            } -->
          </c-card-body>
        </c-card>
      }

      <!-- Web3 Payment -->
      @if (isMethodEnabled('web3_payment')) {
        <c-card
          class="payment-option mb-3 shadow-sm"
          [class.selected]="selectedMethod() === 'web3_payment'"
          (click)="selectMethod('web3_payment')"
        >
          <c-card-body class="d-flex align-items-center">
            <div class="option-icon me-3">
              <svg cIcon name="cryptoWallet"></svg>
            </div>
            <div class="option-content flex-grow-1">
              <h3 class="mb-1">{{ 'payment_web3' | translate }}</h3>
              <p class="mb-0">{{ 'payment_web3_desc' | translate }}</p>
            </div>
            <!-- @if (selectedMethod() === 'web3_payment') {
              <div class="option-check">
                <svg cIcon name="cilCheckCircle" color="success"></svg>
              </div>
            } -->
          </c-card-body>
        </c-card>
      }

      <!-- Credit Card -->
      @if (isMethodEnabled('credit_card') && !platform.is('hybrid')) {
        <c-card
          class="payment-option mb-3 shadow-sm"
          [class.selected]="selectedMethod() === 'credit_card'"
          (click)="selectMethod('credit_card')"
        >
          <c-card-body class="d-flex align-items-center">
            <div class="option-icon me-3">
              <svg cIcon name="cilCreditCard"></svg>
            </div>
            <div class="option-content flex-grow-1">
              <h3 class="mb-1">{{ 'payment_credit_card' | translate }}</h3>
              <p class="mb-0">{{ 'payment_credit_card_desc' | translate }}</p>
            </div>
            <!-- @if (selectedMethod() === 'credit_card') {
              <div class="option-check">
                <svg cIcon name="cilCheckCircle" color="success"></svg>
              </div>
            } -->
          </c-card-body>
        </c-card>
      }

      <!-- Authorize.Net (Credit / Debit Card) -->
      @if (isMethodEnabled('authorize_net') && !platform.is('hybrid')) {
        <c-card
          class="payment-option mb-3 shadow-sm"
          [class.selected]="selectedMethod() === 'authorize_net'"
          (click)="selectMethod('authorize_net')"
        >
          <c-card-body class="d-flex align-items-center">
            <div class="option-icon me-3">
              <svg cIcon name="cilCreditCard"></svg>
            </div>
            <div class="option-content flex-grow-1">
              <h3 class="mb-1">{{ 'payment_authorize_net' | translate }}</h3>
              <p class="mb-0">{{ 'payment_authorize_net_desc' | translate }}</p>
            </div>
          </c-card-body>
        </c-card>
      }
    </div>

    <!-- Wizard Actions -->
    <div class="wizard-actions mt-5 d-flex gap-3">
      <button
        cButton
        color="secondary"
        class="flex-grow-1"
        (click)="goBack()"
        [disabled]="loading()"
      >
        <svg cIcon name="cilArrowLeft" class="me-2"></svg>
        {{ 'payment_back' | translate }}
      </button>

      <button
        cButton
        color="primary"
        class="flex-grow-1"
        (click)="confirmSelection()"
        [disabled]="!canContinue()"
      >
        {{ 'payment_continue' | translate }}
        <!-- <svg cIcon name="cilArrowRight" class="ms-2"></svg> -->
      </button>
    </div>
  }
</div>